// import "./App.scss";
import React from "react";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { ReactQueryDevtools } from "@tanstack/react-query-devtools";
import Appin from "./Appin";
import { AuthProvider } from "./auth/useAuth";
import CatalogContext from "./contexts/CatalogContext";
import "./global.scss";
import "./capacitor.scss";

import ReactGA from "react-ga4";
import { initAnalytics } from "./util/Analytics";
import { CustomerProvider } from "./auth/useCustomer";
import { CatalogSelectionContextProvider } from "./hooks/useCatalogSelectionContext";
import { LogProvider } from "./auth/useLog";
import { BuyingModeProvider } from "./contexts/BuyingModeContext";
import { isNative } from "./util/platform";

const queryClient = new QueryClient();

// ─── Analytics initialization ──────────────────────────────────────────────
//
// Two analytics systems coexist by design (see src/util/Analytics.js header):
//   • react-ga4 — marketing/UTM attribution only
//   • PostHog (via Analytics.js) — all behavioral / product analytics
// Do NOT add new GA4 events. Behavioral events go through PostHog only.
//
// Both are initialized once at module load (this file is the app entry point).
// Failures are isolated and logged so a misconfigured key never crashes the app.

const GA4_TRACKING_ID = "G-9B5G9811WM";

try {
	ReactGA.initialize(GA4_TRACKING_ID);
} catch (err) {
	// Don't let GA4 failure block PostHog or app startup
	console.warn("GA4 initialization failed (analytics-only impact):", err);
}

try {
	initAnalytics();
} catch (err) {
	// PostHog init shouldn't throw on bad config — Analytics.js silent-no-ops on
	// missing key — but defend against an SDK regression or malformed bundle.
	console.warn("PostHog initialization failed (analytics-only impact):", err);
}

function App() {

	const [catalogConfig, setCatalog] = React.useState(null);

	// Initialize Capacitor native plugins
	React.useEffect(() => {
		if (!isNative()) return;

		const cleanups = [];

		const initNativePlugins = async () => {
			// Enable native-only CSS rules
			document.body.classList.add('capacitor-native');

			const [
				{ SplashScreen },
				{ StatusBar, Style },
				{ Keyboard, KeyboardResize },
				{ App: CapApp },
				{ Network },
			] = await Promise.all([
				import('@capacitor/splash-screen'),
				import('@capacitor/status-bar'),
				import('@capacitor/keyboard'),
				import('@capacitor/app'),
				import('@capacitor/network'),
			]);

			await SplashScreen.hide({ fadeOutDuration: 300 });

			// Constrain the WebView below the iOS status bar so app chrome
			// (logo header, hamburger menu, page banners) can never render
			// behind the time / cell-signal indicators. With overlay:false,
			// iOS reserves the status-bar strip for itself and lays out the
			// WebView below it — no per-element safe-area math needed.
			StatusBar.setOverlaysWebView?.({ overlay: false }).catch(() => {});

			// Status bar reserved area gets the WebView container background
			// (white). Use dark icons/text so the indicators stay legible
			// regardless of which catalog (light or dark themed) is loaded.
			StatusBar.setStyle({ style: Style.Dark }).catch(() => {});

			// 'native' mode keeps the document the same size and lets the keyboard
			// overlay content. We compensate per-input below by scrolling the
			// focused field into view (more reliable than 'body' resize, which
			// pushes fixed-position UI off-screen in modal forms).
			Keyboard.setResizeMode?.({ mode: KeyboardResize?.Native ?? 'native' });

			// Scroll focused inputs above the keyboard. The keyboardWillShow event
			// fires before the keyboard is fully on-screen, so we delay one frame
			// to let layout settle, then scroll the active element into view.
			const onKeyboardShow = () => {
				requestAnimationFrame(() => {
					const el = document.activeElement;
					if (el && typeof el.scrollIntoView === 'function') {
						el.scrollIntoView({ block: 'center', behavior: 'smooth' });
					}
				});
			};
			const keyboardListener = await Keyboard.addListener('keyboardWillShow', onKeyboardShow);
			cleanups.push(() => keyboardListener.remove());

			// App lifecycle: when iOS resumes the app from background, dispatch a
			// custom event so feature code (auth refresh, delta sync, etc.) can
			// re-validate. We don't refresh here directly to keep App.js a thin shell.
			const stateListener = await CapApp.addListener('appStateChange', ({ isActive }) => {
				window.dispatchEvent(new CustomEvent('app:state-change', { detail: { isActive } }));
				if (isActive) {
					window.dispatchEvent(new CustomEvent('app:resumed'));
				}
			});
			cleanups.push(() => stateListener.remove());

			// Universal/deep-link entry: Capacitor fires this when the app is opened
			// via an associated domain or custom URL scheme. Components subscribe to
			// 'app:url-open' and route accordingly.
			const urlListener = await CapApp.addListener('appUrlOpen', (event) => {
				window.dispatchEvent(new CustomEvent('app:url-open', { detail: event }));
			});
			cleanups.push(() => urlListener.remove());

			// Anchor click delegation: route external `target="_blank"` links to
			// SFSafariViewController (via @capacitor/browser) so they don't take
			// over the WKWebView. Only intercepts cross-origin http(s) URLs;
			// in-app routes (relative hrefs, links to our own domain) pass through.
			const { Browser } = await import('@capacitor/browser');
			const onAnchorClick = (event) => {
				const anchor = event.target?.closest?.('a[href]');
				if (!anchor) return;
				const href = anchor.getAttribute('href');
				if (!href) return;
				if (href.startsWith('mailto:') || href.startsWith('tel:') || href.startsWith('sms:')) return;
				let url;
				try { url = new URL(href, window.location.href); } catch { return; }
				if (url.protocol !== 'http:' && url.protocol !== 'https:') return;
				if (url.host === window.location.host) return;
				event.preventDefault();
				Browser.open({ url: url.toString(), presentationStyle: 'popover' }).catch(() => {});
			};
			document.addEventListener('click', onAnchorClick, { capture: true });
			cleanups.push(() => document.removeEventListener('click', onAnchorClick, { capture: true }));

			// Network state: Capacitor's Network plugin is more reliable than
			// navigator.onLine on iOS WKWebView (which can lag or report stale state).
			// We mirror connection status onto the body class so SCSS can react, and
			// dispatch synthetic online/offline events so existing listeners (and
			// OfflineStatusIndicator) don't need a separate code path.
			const applyNetworkState = (connected) => {
				document.body.classList.toggle('app-offline', !connected);
				const eventName = connected ? 'online' : 'offline';
				window.dispatchEvent(new Event(eventName));
			};
			Network.getStatus().then(s => applyNetworkState(s.connected)).catch(() => {});
			const networkListener = await Network.addListener('networkStatusChange', (status) => {
				applyNetworkState(status.connected);
			});
			cleanups.push(() => networkListener.remove());
		};

		initNativePlugins().catch(console.warn);

		return () => {
			cleanups.forEach(fn => { try { fn(); } catch {} });
		};
	}, []);

	return (
		<>
			<QueryClientProvider client={queryClient}>
				<AuthProvider>
					<LogProvider>
						<CustomerProvider>
							<BuyingModeProvider>
								<CatalogContext.Provider value={[catalogConfig, setCatalog]}>
									<CatalogSelectionContextProvider>
										<Appin />
									</CatalogSelectionContextProvider>
								</CatalogContext.Provider>
							</BuyingModeProvider>
						</CustomerProvider>
					</LogProvider>
				</AuthProvider>
				{/* <ReactQueryDevtools initialIsOpen={false} position="bottom-right" /> */}
			</QueryClientProvider>
		</>
	);
}

export default App;
